import React, { useState, useEffect, createContext, useContext } from 'react';
import { X, CheckCircle, Warning, Info, XCircle } from 'phosphor-react';
import './styles.css';

/**
 * NotificationToast - Sistema de notificaciones toast mejorado
 * Sigue las convenciones de COPILOT.md
 * 
 * Uso:
 * import { useToast } from './components/NotificationToast';
 * 
 * const { showToast } = useToast();
 * showToast('success', 'Anuncio publicado exitosamente');
 */

// Context para gestionar toasts globalmente
const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast debe usarse dentro de ToastProvider');
  }
  return context;
};

// Toast individual
const Toast = ({ id, type, title, message, duration, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 300); // Tiempo de animación de salida
  };

  const icons = {
    success: <CheckCircle size={24} weight="fill" className="toast__icon--success" />,
    error: <XCircle size={24} weight="fill" className="toast__icon--error" />,
    warning: <Warning size={24} weight="fill" className="toast__icon--warning" />,
    info: <Info size={24} weight="fill" className="toast__icon--info" />
  };

  return (
    <div className={`toast toast--${type} ${isExiting ? 'toast--exiting' : 'toast--entering'}`}>
      <div className="toast__icon">
        {icons[type] || icons.info}
      </div>
      
      <div className="toast__content">
        {title && <div className="toast__title">{title}</div>}
        {message && <div className="toast__message">{message}</div>}
      </div>
      
      <button 
        className="toast__close" 
        onClick={handleClose}
        aria-label="Cerrar notificación"
      >
        <X size={20} weight="bold" />
      </button>
      
      {duration > 0 && (
        <div 
          className="toast__progress"
          style={{ animationDuration: `${duration}ms` }}
        />
      )}
    </div>
  );
};

// Provider y Container
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = (type, message, title = null, duration = 5000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, type, title, message, duration };
    
    setToasts(prev => {
      // Limitar a máximo 5 toasts visibles
      if (prev.length >= 5) {
        return [...prev.slice(1), newToast];
      }
      return [...prev, newToast];
    });
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Shortcuts para tipos comunes
  const toast = {
    success: (message, title = 'Éxito') => showToast('success', message, title),
    error: (message, title = 'Error') => showToast('error', message, title),
    warning: (message, title = 'Advertencia') => showToast('warning', message, title),
    info: (message, title = null) => showToast('info', message, title),
    custom: (type, message, title, duration) => showToast(type, message, title, duration)
  };

  return (
    <ToastContext.Provider value={{ showToast: toast }}>
      {children}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast 
            key={toast.id} 
            {...toast} 
            onClose={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// Componente de ejemplo para testing
export const ToastDemo = () => {
  const { showToast } = useToast();

  return (
    <div style={{ padding: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      <button onClick={() => showToast.success('Anuncio publicado correctamente')}>
        Success Toast
      </button>
      <button onClick={() => showToast.error('No se pudo publicar el anuncio')}>
        Error Toast
      </button>
      <button onClick={() => showToast.warning('El anuncio expirará pronto')}>
        Warning Toast
      </button>
      <button onClick={() => showToast.info('Nueva oferta recibida')}>
        Info Toast
      </button>
    </div>
  );
};

export default ToastProvider;
